import { CircularProgress, FormControl, Grid, MenuItem } from '@material-ui/core';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Form from 'src/components/Form/Form';
import CustomTextField from 'src/components/CustomTextField/custom-textfield.component';
import CustomButton, { ButtonTypeEnum } from 'src/components/CustomButton/custom-button.component';
import { FindInPage, Refresh } from '@material-ui/icons';
import { GetVolumetricWeightFormInitialValues, GetVolumetricWeightFormValidationSchema, IVolumetricWeightRequest } from 'src/models/volumetricWeight.models';
import { CarrierEnum, ServiceEnum, LengthUnitEnum } from 'src/shared/enum';

export interface IVolumetricWeightFormProps {
	getVolumetricWeight: Function;
	setVolumetricWeight: Function;
}

const VolumetricWeightForm : React.FC<IVolumetricWeightFormProps> = props => {
	const { t } = useTranslation();
	const [disabled, setDisabled] = useState<boolean>(false);

	const formik = useFormik<any>({
		initialValues: GetVolumetricWeightFormInitialValues(),
		validationSchema: GetVolumetricWeightFormValidationSchema(t),
		onSubmit: async (values: IVolumetricWeightRequest) => {
			setDisabled(true);
			try {
				await props.getVolumetricWeight(values);
			} catch (e) {
				setDisabled(false);
			} finally {
				setDisabled(false);
			}
		},
	});

	const [showServiceDropdownFlag, setShowServiceDropdownFlag] = useState<CarrierEnum>(CarrierEnum.Delhivery);
	const [selectedService, setSelectedService] = useState<ServiceEnum>(ServiceEnum.Surface);

	useEffect(() => {
		if(showServiceDropdownFlag === CarrierEnum.Delhivery) {
			formik.values.service = selectedService;
		} else {
			formik.values.service = '';
		}
	}, [showServiceDropdownFlag]);

	return (
		<>
			<FormikProvider value={formik}>
				<Form onSubmit={formik.handleSubmit}>
					<Grid container direction='column' spacing={2}>
						<Grid item sm={12}>
							<CustomTextField
								type='number'
								name={'length'}
								label={t('label-length')}
								placeholder={t('label-length')}
								helperText= {LengthUnitEnum.CentiMeter}
								formik={formik}
							/>
						</Grid>
						<Grid item sm={12}>
							<CustomTextField
								type='number'
								name={'width'}
								label={t('label-width')}
								placeholder={t('label-width')}
								helperText={LengthUnitEnum.CentiMeter}
								formik={formik}
							/>
						</Grid>
						<Grid item sm={12}>
							<CustomTextField
								type='number'
								name={'height'}
								label={t('label-height')}
								placeholder={t('label-height')}
								helperText={LengthUnitEnum.CentiMeter}
								formik={formik}
							/>
						</Grid>
						<Grid item container spacing={2}>
							<Grid item sm={6}>
								<FormControl fullWidth variant='standard'>
									<CustomTextField
										name={'carrier'}
										label={t('label-carrier')}
										placeholder={t('label-carrier')}
										formik={formik}
										fullWidth
										select
										children={Object.keys(CarrierEnum).map((unit, index) => (
											<MenuItem key={index} value={CarrierEnum[unit]}>{unit}</MenuItem>
										))}
										onChange={(value: CarrierEnum) => setShowServiceDropdownFlag(value)}
									/>
								</FormControl>
							</Grid>
							<Grid item sm={6}>
								<FormControl fullWidth variant='standard'>
									<CustomTextField
										name={'service'}
										label={t('label-service')}
										placeholder={t('label-service')}
										formik={formik}
										fullWidth
										select
										children={Object.keys(ServiceEnum).map((unit, index) => (
											<MenuItem key={index} value={ServiceEnum[unit]}>{unit}</MenuItem>
										))}
										disabled= {formik.values.carrier !== CarrierEnum.Delhivery}
										onChange={(value: ServiceEnum) => setSelectedService(value)}
									/>
								</FormControl>
							</Grid>
						</Grid>
					</Grid>
					<Grid container item justifyContent='center' spacing={2} className='mt-20'>
						<Grid item>
							<CustomButton
								type={ButtonTypeEnum.Submit}
								disabled={disabled}
								startIcon={!disabled ? <FindInPage /> : <CircularProgress size={20} />}
								buttonText={t('label-get-volumetric-weight')}
							/>
						</Grid>
						<Grid item>
							<CustomButton
								type={ButtonTypeEnum.Reset}
								onClick={() => {
									formik.resetForm();
									props.setVolumetricWeight(null);
								}}
								startIcon={<Refresh />}
								buttonText={t('label-reset')}
								disabled={!formik.dirty || disabled}
							/>
						</Grid>
					</Grid>
				</Form>
			</FormikProvider>
		</>
	);
};

export default VolumetricWeightForm;